import React from "react";
import { createStyles, keyframes } from "antd-style";
// import { getConfigValue } from "@rumble-it/common-lib/src/config";

const CDN_URL = "https://cdn.rumbledirect.com";

type LicensePlateCountry = "NL" | "BE" | "EU";
type LicensePlateSize = "default" | "large" | "small";

export interface LicensePlateProps {
  country?: LicensePlateCountry;
  size?: LicensePlateSize;
  isLoading?: boolean;
  children?: React.ReactNode;
}

const countryStyles: Record<LicensePlateCountry, React.CSSProperties> = {
  NL: {
    backgroundColor: "#f4be04",
    color: "black",
  },
  BE: {
    backgroundColor: "#ffffff",
    color: "#ff0000",
    border: "1px solid #ff0000",
  },
  EU: {
    backgroundColor: "#dddcd8",
    color: "black",
  },
};

const sizeStyles: Record<LicensePlateSize, React.CSSProperties> = {
  default: {
    width: "128px",
    height: "32px",
    lineHeight: "32px",
    paddingLeft: "8px",
    fontSize: "14px",
    fontWeight: 700,
  },
  large: {
    width: "100%",
    lineHeight: "40px",
    paddingLeft: "10px",
    fontSize: "23px",
    fontWeight: 900,
  },
  small: {
    width: "98px",
    height: "32px",
    lineHeight: "32px",
    paddingLeft: "8px",
    fontSize: "14px",
    fontWeight: 700,
  },
};

// ---------------------------------------------
// Styles using antd-style (supports ::before)
// ---------------------------------------------

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const useStyles = createStyles(() => ({
  container: {
    position: "relative",
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "stretch",
    overflow: "hidden",
    borderRadius: "8px",
  },

  leftStrip: {
    width: "22px",
    flexShrink: 0,
    backgroundColor: "#365dab",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
    backgroundSize: "13px auto",
  },

  plateMain: {
    display: "inline-block",
    paddingRight: "8px",
  },

  spinner: {
    position: "absolute",
    right: "12px",
    top: "50%",
    marginTop: "-10px",
    width: "20px",
    height: "20px",
    border: "2px solid black",
    borderBottom: "none",
    animation: `${spin} 1s linear infinite`,
    borderRadius: "100%",
  },
}));

export const LicensePlate = ({
  country = "NL",
  size = "default",
  isLoading = false,
  children,
}: LicensePlateProps) => {
  const { styles } = useStyles();

  const leftSectionImg = `${CDN_URL}/static/assets/license-plates/${country}-licenseplate.png`;

  return (
    <span
      className={styles.container}
      style={{
        ...countryStyles[country],
        ...(size === "large" ? { width: "100%" } : {}),
      }}
    >
      {/* Left EU strip */}
      <span
        className={styles.leftStrip}
        style={{
          backgroundImage: `url('${leftSectionImg}')`,
        }}
      />

      {/* Main plate */}
      <span
        className={styles.plateMain}
        style={{
          ...sizeStyles[size],
        }}
      >
        {children}
      </span>

      {/* Loading spinner */}
      {isLoading && <div className={styles.spinner} />}
    </span>
  );
};
